import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Label from 'LuxKit/Label'
import LabelLoadingBox from 'LuxKit/Label/LabelLoadingBox'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import { rem } from 'polished'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import List from 'components/Common/List'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchAllVouchers } from 'actions/MarketingAction'
import { getCanRedeemLuxPlusBenefits, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN, LUXURY_PLUS } from 'luxPlus/constants/base'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalContext from 'contexts/ModalContext'
import LuxPlusFreePreviewModal from 'luxPlus/components/modals/LuxPlusFreePreviewModal'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { selectLoggedIn } from 'selectors/accountSelectors'

const LABEL_LOADING_WIDTH = 88

const CircularIcon = styled(Sheet)`
  display: grid;
  place-items: center;
  height: ${rem(32)};
  width: ${rem(32)};
  flex-shrink: 0;
  ${setSvgIconSize(20)}
`

interface FlightVoucherProps {
  title: React.ReactNode
  subtitle: React.ReactNode
  available: number
  isLoading?: boolean
}

function FlightVoucher({ title, subtitle, available, isLoading }: FlightVoucherProps) {
  return (
    <Sheet
      padding={16}
      borderRadius="L"
      border="neutral-six"
    >
      <Group direction="horizontal" gap={12} wrap="wrap">
        <Group direction="horizontal" grow>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <CircularIcon borderRadius="round" background="tier-silver-dark">
              <SolidPlaneDepartureIcon colour="neutral-eight" />
            </CircularIcon>
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">{title}</BodyText>
              <BodyText variant="large">{subtitle}</BodyText>
            </Group>
          </Group>
        </Group>
        <Group direction="vertical" verticalAlign="center">
          {isLoading && <LabelLoadingBox width={LABEL_LOADING_WIDTH} />}
          {!isLoading && <Label kind="primary" variant="highlight">
            <FormattedMessage
              id="bGM50zbTHZYTR2cb6LrQ"
              defaultMessage="{count} available"
              description="Label showing number of flight vouchers available in the modal"
              values={{ count: available }}
            />
          </Label>}
        </Group>
      </Group>
    </Sheet>
  )
}

function LuxPlusFlightVoucherModal() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)

  const allVouchers = useAppSelector(state => state.marketing.allVouchers)
  const allVouchersStatus = useAppSelector(state => state.marketing.allVouchersStatus)
  const isFetching = useAppSelector(state => state.marketing.isFetchingAllVouchers)
  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isFreePreviewMember = useAppSelector(getIsFreePreviewLuxPlusMember)
  const isLoggedIn = useAppSelector(selectLoggedIn)

  useEffect(() => {
    if (allVouchersStatus !== 'redeemed' && !isFetching) {
      dispatch(fetchAllVouchers('redeemed'))
    }
  }, [dispatch, allVouchersStatus, isFetching])

  const isVouchersLoaded = allVouchersStatus === 'redeemed' && !isFetching

  const redeemedFlightVoucherCount = useMemo(() => {
    if (!isLuxPlusMember) return 0

    return allVouchers.filter(v =>
      v.voucherTitle === LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.VOUCHER_TITLE &&
      v.voucherToggles.includes('is-caused-by-loyalty'),
    ).length
  }, [allVouchers, isLuxPlusMember])

  const availableFlightVoucherCount = Math.max(
    0,
    LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.DEFAULT_VOUCHER_COUNT - redeemedFlightVoucherCount,
  )

  const handleBecomeMemberAndClose = useCallback(() => {
    resolve()
    handleBecomeMember()
  }, [resolve, handleBecomeMember])

  const handleStartFreePreview = useCallback(async() => {
    if (!isLoggedIn) {
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!loginResult?.loggedIn) return
    }
    resolve()
    await showModal<boolean>(<LuxPlusFreePreviewModal />)
  }, [showModal, resolve, isLoggedIn])

  const titleText = <h3 style={{ font: 'inherit', margin: 0 }}>
    {intl.formatMessage({
      id: 'alAk1RF6FtcLL7hpky5Z',
      defaultMessage: 'Member Discounts on International Return Flights',
      description: 'Title of the modal for LuxPlus+ flight voucher benefit for members',
    })}
  </h3>

  const becomeMemberText = intl.formatMessage({
    id: 'CPWKZ6jmP7Oh_AltrLGu',
    defaultMessage: 'Become a member',
    description: 'Primary action button text for flight voucher modal when user is not a LuxPlus member',
  })

  const startFreePreviewText = intl.formatMessage({
    id: '3w5vhugunuQXjdxLChfu',
    defaultMessage: 'Start free preview',
    description: 'Secondary action button text for flight voucher modal when user is not a LuxPlus member',
  })

  const searchFlightsText = intl.formatMessage({
    id: 'TmUcBkoEkZ1P5HUKV5Dq',
    defaultMessage: 'Search flights',
    description: 'Primary action button text for flight voucher modal when user is a LuxPlus member',
  })

  const bold = (chunks: React.ReactNode) => <strong>{chunks}</strong>

  return (
    <Modal
      title={titleText}
      primaryActionText={isLuxPlusMember ? searchFlightsText : becomeMemberText}
      primaryActionTo={isLuxPlusMember ? '/flights' : undefined}
      primaryActionTarget={isLuxPlusMember ? '_blank' : undefined}
      onPrimaryActionClick={isLuxPlusMember ? undefined : handleBecomeMemberAndClose}
      secondaryActionText={isLuxPlusMember || isFreePreviewMember ? undefined : startFreePreviewText}
      onSecondaryActionClick={isLuxPlusMember || isFreePreviewMember ? undefined : handleStartFreePreview}
    >
      <Group direction="vertical" gap={16}>
        <BodyText variant="large">
          <FormattedMessage
            id="e0mjewUinwrmG7watXWc"
            defaultMessage="As a {luxPlus} member, enjoy exclusive savings on international return flights departing Australia. For a limited time, receive 2 international flight vouchers to use on eligible bookings. Offer ends {endDate}."
            description="Subtitle explaining the LuxPlus+ flight voucher benefit"
            values={{
              luxPlus: LUXURY_PLUS.PROGRAM_NAME,
              endDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE} day="numeric" month="long" year="numeric" />,
            }}
          />
        </BodyText>

        <FlightVoucher
          title={<FormattedMessage
            id="ph9UYyEWnSdjsSVrA0-u"
            defaultMessage="$50-$400 Voucher"
            description="Title of the $50-$400 flight voucher card for members"
          />}
          subtitle={<FormattedMessage
            id="VnZSQ3GVibCiHe7elF04"
            defaultMessage="International Return Flights"
            description="Subtitle for international return flights flight voucher"
          />}
          available={availableFlightVoucherCount}
          isLoading={isLuxPlusMember && !isVouchersLoaded}
        />

        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="cNA0jPCjSyxShgnSb8jz"
            defaultMessage="Offer"
            description="Section heading for offer in flight voucher modal for members"
          />
        </BodyText>

        <BodyText variant="large">
          <FormattedMessage
            id="gO7mbZuU7Fuqx00_ZKg6"
            defaultMessage="Save on eligible international return flights departing Australia:"
            description="Intro text for offer section in the flight voucher modal"
          />
        </BodyText>

        <List type="ul">
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="X_5S0lbaXp-QVfjhYaxg"
              defaultMessage="<bold>Economy:</bold> $50 off per passenger"
              description="Discount amount by this voucher for economy class flights for members"
              values={{ bold }}
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="u99vpF-rKcZs9fZqaVnu"
              defaultMessage="<bold>Premium Economy:</bold> $100 off per passenger"
              description="Discount amount by this voucher for premium economy class flights for members"
              values={{ bold }}
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="8lDn9puaW_RUcGQiT_A_"
              defaultMessage="<bold>Business Class:</bold> $200 off per passenger"
              description="Discount amount by this voucher for business class flights for members"
              values={{ bold }}
            />
          </BodyText>
        </List>

        <BodyText variant="large">
          <FormattedMessage
            id="QmmG_R1s4YXGWQV8vHAb"
            defaultMessage="Each voucher can be applied to up to 2 passengers per booking."
            description="Maximum passengers per voucher for flight voucher discount"
          />
        </BodyText>

        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="-6Dow6x9nC_9VrahjF8U"
            defaultMessage="Eligibility"
            description="Section heading for eligibility in flight voucher modal"
          />
        </BodyText>

        <List type="ul">
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="RdFkUox5KvaU01BJtavw"
              defaultMessage="Available to Australian {luxPlus} members only"
              description="Eligibility requirement for Australian LuxPlus+ members"
              values={{ luxPlus: LUXURY_PLUS.PROGRAM_NAME }}
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="RazpS6pg3Fi7c__UbiLo"
              defaultMessage="Valid on international return flights departing Australia"
              description="Eligibility requirement for international return flights departing Australia"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="6CaHqGBvUzzrLOIt33Zz"
              defaultMessage="Each voucher can be used once"
              description="Eligibility requirement: each voucher can be used once"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="mO6sVxILD6YkEMA-4G0R"
              defaultMessage="Maximum 2 passengers per voucher"
              description="Eligibility requirement: max passengers per voucher"
            />
          </BodyText>
        </List>

        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="4-n5f-Wjn6Dz-qbSC8w3"
            defaultMessage="How it works"
            description="Section heading for how it works in flight voucher modal"
          />
        </BodyText>

        <List type="ul">
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="_0xGth_9QggsjZdumdac"
              defaultMessage="Select an eligible international return flight departing Australia"
              description="Step 1 of how to redeem the flight voucher for users"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="fjFV6Sd1eRr34jLZYjk9"
              defaultMessage="Choose your voucher during checkout"
              description="Step 2 of how to redeem the flight voucher for users"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="l1v-nuEk5z8SZrg47S-U"
              defaultMessage="Your discount will be applied automatically based on the selected cabin class"
              description="Step 3 of how to redeem the flight voucher for users"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="hoeWwtUfSfTPfECYOUKa"
              defaultMessage="Only one voucher can be applied per booking. Vouchers cannot be combined or stacked"
              description="Step 4 of how to redeem the flight voucher for users"
            />
          </BodyText>
        </List>

        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="Uap2R2gF5Hbq8p0rWohL"
            defaultMessage="Exclusions"
            description="Section heading for exclusions in flight voucher modal"
          />
        </BodyText>

        <BodyText variant="large">
          <FormattedMessage
            id="MpeVE7SXDyKi4Pt0n44-"
            defaultMessage="This voucher is not valid on:"
            description="Exclusion showing what this voucher is not valid on"
          />
        </BodyText>

        <List type="ul">
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="ruJPSUZf2J37gWd5a0co"
              defaultMessage="Jetstar or Virgin Australia operated flights"
              description="Exclusion for Jetstar and Virgin Australia operated flights"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="jc4FSXn0ogM3KEuSTSAz"
              defaultMessage="Any itinerary not originating in Australia"
              description="Exclusion for itineraries not originating in Australia"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="oo6DuQtYhA1KbGuvjiJW"
              defaultMessage="Flights departing from or arriving in New Zealand"
              description="Exclusion for flights departing from or arriving in New Zealand"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="3XdGzLLJDrotxBixWr-X"
              defaultMessage="Flights to or within the following South Pacific destinations:"
              description="Exclusion intro for South Pacific destination restrictions"
            />
          </BodyText>
          <List type="ul">
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="qx64rZ-jXdKzoozWEqEP"
                defaultMessage="Australia"
                description="Exclusion destination: flights to or within Australia"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="ZjZ6iJ94p-APaIuOYD1N"
                defaultMessage="Cook Islands"
                description="Exclusion destination: flights to or within Cook Islands"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="XMNlu1JikbDWQ3fhFJQC"
                defaultMessage="Fiji"
                description="Exclusion destination of flights voucher: flights to or within Fiji"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="517G5PaaDqLUTYyse-DW"
                defaultMessage="French Polynesia"
                description="Exclusion destination: flights to or within French Polynesia"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="Xg3r0qErJH0s9RqnNmS4"
                defaultMessage="Indonesia (including Bali)"
                description="Exclusion destination of flights voucher: flights to or within Indonesia including Bali"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="90PZAW9F3sy5rH5SxZEz"
                defaultMessage="New Caledonia"
                description="Exclusion destination: flights to or within New Caledonia"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="TO18Y7GH1yNlrswvlEi7"
                defaultMessage="New Zealand"
                description="Exclusion destination: flights to or within New Zealand"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="RHXZHppQsKUOhCPdihbx"
                defaultMessage="Niue"
                description="Exclusion destination of flights voucher: flights to or within Niue"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="r6unBoXdC2vOwV-uw6-a"
                defaultMessage="Samoa"
                description="Exclusion destination of flights voucher: flights to or within Samoa"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="3gcPirUw4G5ogINi4LBD"
                defaultMessage="Solomon Islands"
                description="Exclusion destination of flights voucher: flights to or within Solomon Islands"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="6LSRT9ajFkjA97k5iXnw"
                defaultMessage="Tonga"
                description="Exclusion destination of flights voucher: flights to or within Tonga"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="-R4GDye5IOcpKQpd7JOd"
                defaultMessage="Tuvalu"
                description="Exclusion destination: flights to or within Tuvalu"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="Ggl0tbpJqNIRD5q0LdyW"
                defaultMessage="Vanuatu"
                description="Exclusion destination: flights to or within Vanuatu"
              />
            </BodyText>
            <BodyText as="li" variant="large">
              <FormattedMessage
                id="k1UrJsoSTWs3Dn5lsANl"
                defaultMessage="Wallis and Futuna"
                description="Exclusion destination: flights to or within Wallis and Futuna"
              />
            </BodyText>
          </List>
        </List>

        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="NCDfLMPkPVG-1ycAgd4d"
            defaultMessage="Expiry"
            description="Section heading for expiry in flight voucher modal"
          />
        </BodyText>

        <List type="ul">
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="8oJLKiAIYsjx4UVmjdrY"
              defaultMessage="Vouchers are valid from {startDate} until 11:59pm AEDT on {endDate}"
              description="Showing Validity date range for flight vouchers in modal"
              values={{
                startDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.START_DATE} format="dmy-casual" />,
                endDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE} format="dmy-casual" />,
              }}
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="pSVOFLb5evF3bcs57KkP"
              defaultMessage="Voucher must be applied before expiry"
              description="Showing Expiry requirement for flight vouchers in modal"
            />
          </BodyText>
        </List>

        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="TUeT3mYmju2qSI0Bk9vr"
            defaultMessage="Important information"
            description="Section heading for important information in flight voucher in modal"
          />
        </BodyText>

        <List type="ul">
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="hsh8i2G-pDn1DfXDaKKs"
              defaultMessage="Voucher is non-transferable and not redeemable for cash"
              description="Showing Non-transferable voucher policy for flight vouchers in modal"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="d5jeC75vKeLchUPOxIZ9"
              defaultMessage="Cannot be combined with other flight vouchers or promo codes"
              description="Showing Voucher combination restriction for flight vouchers in modal"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="SQFsoQA7one9B_rEjetv"
              defaultMessage="If a booking is cancelled, the voucher will not be reissued"
              description="Showing Voucher cancellation policy for flight vouchers in modal"
            />
          </BodyText>
          <BodyText as="li" variant="large">
            <FormattedMessage
              id="H4v097jBKM5MfmUwzyRY"
              defaultMessage="Airline fare rules and booking conditions apply"
              description="Showing Airline rules disclaimer for flight vouchers in modal"
            />
          </BodyText>
        </List>
      </Group>
    </Modal>
  )
}

export default LuxPlusFlightVoucherModal
